import { useCallback, useRef } from "react";
import { doc, serverTimestamp, updateDoc, deleteField, type Firestore } from "firebase/firestore";
import { computeBillingSteps } from "../lib/billing";
import { createItemWithSerialClaim } from "../services/createItem";
import { ensureAssetIdForItem } from "../services/assetIdClient";
import { buildDisplaySerial } from "../services/serialIndex";
import { uploadPhotosForItem, type PhotoKey } from "../services/photoUploads";
import { createEmptyRegistrationForm } from "../lib/registrationForm";

type FormState = {
  orderNumber: string;
  manufacturer: string;
  model: string;
  productType: string;
  productTypeId?: string;
  warrantyStartDate: string;
  serial: string;
  chargerIncluded: boolean;
  adapterYesNo: string;
  damageNotes: string;
  customer: string;
  customerKey?: string;
  articleNumber: string;
  photos: Record<string, string | null>;
  reuse: boolean;
  resold: boolean;
  scrap: boolean;
  salesBatchId?: string;
  salesBatchName?: string;
  salesKolliId?: string;
  salesKolliName?: string;
  salesCpu?: string;
  salesStorage?: string;
  salesRam?: string;
};

type Params = {
  db: Firestore;
  storage: any;
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  draftItemId: string | null;
  ensureDraftItem: () => Promise<string>;
  aiSuggestions: any;
  rpCustomerOpts: { key: string; name: string }[];
  splitSerialParts: (s: string) => { base: string; visit: number };
  normalizeSerialKey: (s: string) => string;
  setUploadProgress: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  currentUserString: () => string | null;
  refreshHomeList: () => Promise<void>;
  setThumbnailPreviews: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
  setDraftItemId: React.Dispatch<React.SetStateAction<string | null>>;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  setIsSaving: (v: boolean) => void;
  isSaving: boolean;
  ensureProductTypeInDb: (id: string, label?: string) => Promise<any>;
};

export function useCreateItemFlow({
  db,
  storage,
  form,
  setForm,
  draftItemId,
  ensureDraftItem,
  aiSuggestions,
  rpCustomerOpts,
  splitSerialParts,
  normalizeSerialKey,
  setUploadProgress,
  currentUserString,
  refreshHomeList,
  setThumbnailPreviews,
  setDraftItemId,
  setCurrentStep,
  setIsSaving,
  isSaving,
  ensureProductTypeInDb,
}: Params) {
  const saveInFlightRef = useRef(false);

  const handleInputChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ): void => {
    const t = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    const { name, type, value } = t;
    const v = type === "checkbox" ? (t as HTMLInputElement).checked : value;
    setForm((prev) => ({ ...prev, [name]: v }));
  }, [setForm]);

  const saveData = useCallback(async () => {
    if (saveInFlightRef.current || isSaving) return;
    if (!form.manufacturer || !form.model || !form.serial) {
      alert("Fyll i tillverkare, modell och serienummer.");
      return;
    }

    saveInFlightRef.current = true;
    setIsSaving(true);
    setUploadProgress({});

    try {
      // React-state uppdateras asynkront. Använd därför det ID som ensureDraftItem
      // returnerar direkt, så snabbflödet sparar i samma dokument som draften.
      const activeDraftItemId = draftItemId || await ensureDraftItem();

      const steps = computeBillingSteps({
        reuse: !!form.reuse,
        resold: !!form.resold,
        scrap: !!form.scrap,
      });

      const _parts = splitSerialParts(String(form.serial || ""));
      const baseSerial = _parts.base;
      const baseKey = normalizeSerialKey(baseSerial);

      if (!baseKey) {
        console.warn("[saveData] Serienummer saknas eller kunde inte normaliseras “ avbryter commit.");
        try { (window as any).toast?.("Ange ett giltigt serienummer.", { type: "warning" }); } catch { /* ignore */ }
        return;
      }

      const { itemId, finalSerial } = await createItemWithSerialClaim(
        { db, currentUserString, ensureProductTypeInDb },
        {
          draftItemId: activeDraftItemId,
          form,
          aiSuggestions,
          rpCustomerOpts,
          steps,
          splitSerialParts,
          normalizeSerialKey,
          buildDisplaySerial,
        }
      );

      // Säkerställ assetId direkt (callable på servern)
      try {
        await ensureAssetIdForItem(itemId);
      } catch (e) {
        console.warn("[saveData] kunde inte tilldela assetId direkt", e);
      }

      const itemRef = doc(db, "itInventory", itemId);
      await updateDoc(itemRef, { expiresAt: deleteField(), updatedAt: serverTimestamp() });

      const photoResult = await uploadPhotosForItem({
        photos: form.photos,
        storage,
        itemRef,
        setUploadProgress,
      });

      if (photoResult.failed.length > 0) {
        const labels: Record<PhotoKey, string> = {
          keyboard: "Tangentbord",
          screen: "Skärm",
          underside: "Undersida",
          topside: "Ovansida",
        };
        const failedLabels = photoResult.failed.map((key) => labels[key]).join(", ");
        alert(
          `Enhet sparad som ${finalSerial}.\n\n` +
          `Följande valda bilder kunde inte sparas: ${failedLabels}. ` +
          "Lägg till dem via Redigera enhet."
        );
      } else {
        alert(`Enhet sparad som ${finalSerial}.`);
      }

      setForm(createEmptyRegistrationForm());
      setDraftItemId(null);
      setThumbnailPreviews({});
      await refreshHomeList();
      setCurrentStep(1);
    } catch (err: any) {
      console.error(err);
      alert("Kunde inte spara: " + (err?.message || err));
    } finally {
      saveInFlightRef.current = false;
      setIsSaving(false);
    }
  }, [
    aiSuggestions,
    currentUserString,
    draftItemId,
    form,
    normalizeSerialKey,
    refreshHomeList,
    setCurrentStep,
    setDraftItemId,
    setForm,
    setIsSaving,
    setThumbnailPreviews,
    setUploadProgress,
    splitSerialParts,
    storage,
    rpCustomerOpts,
    db,
    ensureDraftItem,
    ensureProductTypeInDb,
    isSaving,
  ]);

  const handleSave = useCallback(async () => { await saveData(); }, [saveData]);

  return { handleInputChange, handleSave };
}
